import { FC, memo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { Block } from '../../pages/pages.styled';
import { Text, Button } from '../../components-v4';
import {
  List,
  ItemWrapper,
  Informer,
  Icon,
  SectorInfoWrapper,
} from './accruals.styled';
import { useAppDispatch, useResponsive } from '../../../../hooks';
import debtIcon from '../../assets/x-circle-filled.svg';
import { integerDeclination } from '../../utils';
import {
  getOnLoadPublicRequestAccrualsState,
  getAccrualsData,
  getPublicId,
} from '../../selectors';
import { MIN_SKELETON_DURATION } from '../../constants';
import { AccrualsSkeleton } from '../skeletons';
import { toFractionSpaceRubles } from '../../../../utils/getters';
import { createPaymentLink } from '../../request-single-page-slice';

export const Accruals: FC = memo(() => {
  const dispatch = useAppDispatch();
  const { viewport, device } = useResponsive();
  const accrualsData = useSelector(getAccrualsData);
  const publicRequestId = useSelector(getPublicId);
  const onLoadAccruals = useSelector(getOnLoadPublicRequestAccrualsState);

  const [showSkeleton, setShowSkeleton] = useState<boolean>(onLoadAccruals);
  const loadStartRef = useRef<number | null>(
    onLoadAccruals ? Date.now() : null
  );

  const handlePay = (sector: string) => {
    dispatch(
      createPaymentLink({
        id: publicRequestId,
        sector_code: sector,
      })
    )
      .then(unwrapResult)
      .then((response) => {
        const link = document.createElement('a');
        link.href = response.bank_url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
      });
  };

  useEffect(() => {
    if (onLoadAccruals) {
      loadStartRef.current = Date.now();
      setShowSkeleton(true);
    } else if (loadStartRef.current !== null) {
      const elapsed = Date.now() - loadStartRef.current;
      const remaining = MIN_SKELETON_DURATION - elapsed;

      if (remaining > 0) {
        const timer = setTimeout(() => setShowSkeleton(false), remaining);
        return () => clearTimeout(timer);
      }

      setShowSkeleton(false);
    }
  }, [onLoadAccruals]);

  if (showSkeleton) {
    return <AccrualsSkeleton />;
  }

  return (
    <Block id="accruals">
      <Text variant="heading-3" color="gray.800">
        Начисления
      </Text>
      <List>
        <Informer hasDebt={true}>
          <Icon src={debtIcon} />
          <Text
            variant={viewport.mobile ? 'body-14' : 'body-16'}
            color="gray.800"
          >
            У вас {accrualsData.length}{' '}
            {integerDeclination(accrualsData.length, [
              'задолженность',
              'задолженности',
              'задолженностей',
            ])}{' '}
            по начислениям
          </Text>
        </Informer>
        {accrualsData.map((item) => (
          <ItemWrapper key={item.sector_code}>
            <SectorInfoWrapper>
              <Text
                variant={viewport.mobile ? 'body-14' : 'body-16'}
                color="gray.800"
              >
                {item.sector_code_ru}
              </Text>
              <Text
                variant={viewport.mobile ? 'body-14' : 'body-16'}
                color="red.500"
              >
                {toFractionSpaceRubles(item.balance)} ₽
              </Text>
            </SectorInfoWrapper>
            <Button
              size="l"
              mode="additional"
              variant="main"
              onClick={() => handlePay(item.sector_code)}
            >
              Оплатить
            </Button>
          </ItemWrapper>
        ))}
      </List>
    </Block>
  );
});
